
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { SheetContent, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { LogOut, User, Headphones, Target, Crown, CreditCard, Shield, Download, Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useImageUrl } from '@/hooks/useImageUrl';
import { debugLogger } from '@/utils/debugLogger';
import { cn } from '@/lib/utils';
import { useUserRole } from '@/hooks/useUserRole';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { InstallInstructionsModal } from '@/components/pwa/InstallInstructionsModal';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useBuildInfo } from '@/hooks/useBuildInfo';

// Helper: Wait for next animation frame
const nextFrame = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

// Helper: Sleep for ms
const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

// Parse CSS duration/delay strings (e.g., "300ms", "0.3s", "200ms, 300ms")
const parseCSSDurations = (value: string): number[] => {
  if (!value || value === '0s') return [0];
  return value.split(',').map(v => {
    const trimmed = v.trim();
    if (trimmed.endsWith('ms')) return parseFloat(trimmed);
    if (trimmed.endsWith('s')) return parseFloat(trimmed) * 1000;
    return 0;
  });
};

// Get total close animation time from element
const getCloseAnimationMs = (element: HTMLElement | null): number => {
  if (!element) return 300; // Fallback
  
  const computed = getComputedStyle(element);
  const animDurations = parseCSSDurations(computed.animationDuration);
  const animDelays = parseCSSDurations(computed.animationDelay);
  const transDurations = parseCSSDurations(computed.transitionDuration);
  const transDelays = parseCSSDurations(computed.transitionDelay);
  
  const allTotals = [
    ...animDurations.map((d, i) => d + (animDelays[i] || 0)),
    ...transDurations.map((d, i) => d + (transDelays[i] || 0))
  ];
  
  const maxTotal = Math.max(...allTotals, 0);
  return maxTotal > 0 ? maxTotal : 300; // Fallback to 300ms
};

interface MobileMenuManagerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MobileMenuManager: React.FC<MobileMenuManagerProps> = ({ isOpen, onOpenChange }) => {
  const closingRef = useRef(false); // Transition guard
  const sheetContentRef = useRef<HTMLDivElement>(null); // Ref to measure animation
  const { user, profileData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { canShowInstallButton, promptInstall, platform } = usePWAInstall();
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);

  // Navigate after ensuring menu closes smoothly
  const navigateAfterClose = useCallback(async (to: string) => {
    // Set transition guard to prevent reopening
    closingRef.current = true;
    debugLogger.debug('MobileMenuManager: Closing menu for navigation', { to });

    // Force immediate close
    flushSync(() => {
      onOpenChange(false);
    });

    // Wait for at least one paint cycle
    await nextFrame();
    
    // Measure actual animation duration
    const measured = getCloseAnimationMs(sheetContentRef.current);
    const waitMs = measured + 50; // Add small buffer
    debugLogger.debug('MobileMenuManager: Measured animation', { measured, waitMs });
    
    // Wait for animation to complete
    await sleep(waitMs);

    // Navigate
    navigate(to);

    // Clear guard
    setTimeout(() => {
      closingRef.current = false;
      debugLogger.debug('MobileMenuManager: Transition guard cleared');
    }, 50);
  }, [navigate, onOpenChange]);

  // Guarded onOpenChange - prevents reopening during transition
  const onOpenChangeGuarded = useCallback((open: boolean) => {
    if (open && closingRef.current) {
      debugLogger.debug('MobileMenuManager: Blocked reopen during transition');
      return;
    }
    onOpenChange(open);
  }, [onOpenChange]);

  const handleProfileClick = useCallback(() => {
    navigateAfterClose('/main/profile');
  }, [navigateAfterClose]);

  const handleMissionsClick = useCallback(() => {
    navigateAfterClose('/main/missions');
  }, [navigateAfterClose]);

  const handleWheelClick = useCallback(() => {
    navigateAfterClose('/main/wheel');
  }, [navigateAfterClose]);

  const handleVerificationClick = useCallback(() => {
    navigateAfterClose('/main/verify');
  }, [navigateAfterClose]);

  const handlePlansClick = useCallback(() => {
    navigateAfterClose('/main/subscriptions');
  }, [navigateAfterClose]);

  const handleAdminClick = useCallback(() => {
    navigateAfterClose('/main/joia/admin/overview');
  }, [navigateAfterClose]);

  const handleSupportClick = useCallback(() => {
    navigateAfterClose('/main/support');
  }, [navigateAfterClose]);

  const handleLogout = useCallback(() => {
    debugLogger.debug('MobileMenuManager: Logout requested');
    navigateAfterClose('/main/logout');
  }, [navigateAfterClose]);

  const handleInstallApp = useCallback(async () => {
    debugLogger.debug('MobileMenuManager: Install app requested');
    
    // iOS/Safari: show instructions modal
    if (platform.isIOS || platform.isSafari) {
      debugLogger.info('PWA', 'Showing iOS install instructions');
      setShowInstallInstructions(true);
      return;
    }
    
    // Chrome/Edge: trigger native prompt
    const success = await promptInstall();
    
    if (success) {
      onOpenChange(false);
    } else {
      // Fallback: show instructions if prompt failed
      debugLogger.warn('PWA', 'Native prompt failed, showing instructions');
      setShowInstallInstructions(true);
    }
  }, [promptInstall, onOpenChange, platform.isIOS, platform.isSafari]);

  // Clear guard when route changes (failsafe)
  useEffect(() => {
    closingRef.current = false;
    debugLogger.debug('MobileMenuManager: Route changed, guard cleared', { 
      pathname: location.pathname 
    });
  }, [location.pathname]);

  // Check if user can access admin panel (admin or moderator)
  const { canAccessAdminPanel: canAccessAdmin } = useUserRole();
  
  // Build info for version display
  const { displayVersion, displayBuildTime, isLovableEnv } = useBuildInfo();

  const displayName = profileData?.username || user?.email?.split('@')[0] || 'Usuário';
  
  // Use async hook for v1.5 signed URL support
  const { url: avatarPhoto } = useImageUrl(
    profileData?.main_photo_url,
    'avatar'
  );
  
  const userLocation = profileData?.city && profileData?.state_code
    ? `${profileData.city}, ${profileData.state_code}` 
    : (profileData?.city || '');

  // Debug log to track profileData updates
  React.useEffect(() => {
    debugLogger.debug('MobileMenuManager: profileData updated', {
      username: profileData?.username,
      city: profileData?.city,
      state_code: profileData?.state_code,
      main_photo_url: profileData?.main_photo_url,
    });
  }, [profileData]);

  return (
    <SheetContent
      ref={sheetContentRef}
      side="right" 
      className="w-[320px] bg-black border-l border-white/10 text-white p-0 flex flex-col"
    >
      <SheetDescription className="sr-only">
        Menu de navegação com acesso ao perfil, missões, verificação, assinaturas e suporte.
      </SheetDescription>
      
      {/* Fixed Header with Avatar */}
      <div className="flex-shrink-0 bg-white/5 p-6 border-b border-white/10">
        <div className="flex items-center space-x-4">
          <Avatar className="h-12 w-12 ring-2 ring-luxury-pinkBright/40">
            <AvatarImage 
              src={avatarPhoto}
              alt={displayName}
            />
            <AvatarFallback className="bg-luxury-pinkBright text-white font-semibold text-lg">
              {displayName[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg text-white truncate">
              {displayName}
            </h3>
            <p className="text-sm text-white/60 truncate">
              {userLocation || user?.email}
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Menu Items */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-6 space-y-2">
          {/* Profile Section */}
          <div className="mb-6">
            <h4 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-3">
              Perfil
            </h4>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start text-white hover:bg-white/5 hover:text-white transition-all duration-200",
                location.pathname === '/main/profile' && "bg-luxury-pinkBright text-white"
              )}
              onClick={handleProfileClick}
            >
              <User className="h-5 w-5 mr-3" />
              Editar Perfil
            </Button>
          </div>

          {/* Actions Section */}
          <div className="mb-6">
            <h4 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-3">
              Ações
            </h4>
            <div className="space-y-1">
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start text-white hover:bg-white/5 hover:text-white transition-all duration-200",
                  location.pathname === '/main/missions' && "bg-luxury-pinkBright text-white"
                )}
                onClick={handleMissionsClick}
              >
                <Target className="h-5 w-5 mr-3" />
                Missões
              </Button>

              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start text-white hover:bg-white/5 hover:text-white transition-all duration-200",
                  location.pathname === '/main/wheel' && "bg-luxury-pinkBright text-white"
                )}
                onClick={handleWheelClick}
              >
                <Sparkles className="h-5 w-5 mr-3" />
                Roleta da Sorte
              </Button>
              
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start text-white hover:bg-white/5 hover:text-white transition-all duration-200",
                  location.pathname === '/main/verify' && "bg-luxury-pinkBright text-white"
                )}
                onClick={handleVerificationClick}
              >
                <Shield className="h-5 w-5 mr-3" />
                Verificação
              </Button>

              {/* Plans - Show for all users */}
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start text-white hover:bg-white/5 hover:text-white transition-all duration-200",
                  location.pathname === '/main/subscriptions' && "bg-luxury-pinkBright text-white"
                )}
                onClick={handlePlansClick}
              >
                <CreditCard className="h-5 w-5 mr-3" />
                Assinaturas
              </Button>

              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start text-white hover:bg-white/5 hover:text-white transition-all duration-200",
                  location.pathname === '/main/support' && "bg-luxury-pinkBright text-white"
                )}
                onClick={handleSupportClick}
              >
                <Headphones className="h-5 w-5 mr-3" />
                Suporte
              </Button>

              {canShowInstallButton && (
                <Button
                  variant="ghost"
                  className="w-full justify-start text-white hover:bg-white/5 hover:text-white transition-all duration-200"
                  onClick={handleInstallApp}
                >
                  <Download className="h-5 w-5 mr-3" />
                  Instalar App
                </Button>
              )}
            </div>
          </div>

          {/* Admin Section - Only show for admin or moderator role */}
          {canAccessAdmin && (
            <div className="mb-6">
              <h4 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-3">
                Administração
              </h4>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start text-white hover:bg-white/5 hover:text-white transition-all duration-200",
                  location.pathname.startsWith('/main/joia/admin') && "bg-luxury-pinkBright text-white"
                )}
                onClick={handleAdminClick}
              >
                <Crown className="h-5 w-5 mr-3" />
                Painel Administrativo
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Fixed Logout Button at Bottom */}
      <div className="flex-shrink-0 p-6 border-t border-white/10 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        <Button
          variant="ghost"
          className="w-full justify-start text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-all duration-200"
          onClick={handleLogout}
        >
          <LogOut className="h-5 w-5 mr-3" />
          Sair
        </Button>
        
        {/* Build Info */}
        <div className="mt-4 text-center">
          <p className="text-[10px] text-white/30 font-mono">
            {isLovableEnv ? (
              <>Ambiente Lovable Preview</>
            ) : (
              <>
                Versão: {displayVersion}<br />
                Build: {displayBuildTime}
              </>
            )}
          </p>
        </div>
      </div>
      
      <InstallInstructionsModal
        open={showInstallInstructions}
        onOpenChange={setShowInstallInstructions}
        platform={platform}
      />
    </SheetContent>
  );
};

export default MobileMenuManager;
